import { createFeatureSelector, createSelector } from "@ngrx/store";
import * as fromAuth from "./auth.reducer";
import { isNullable } from "@kells/utils/js";

export const selectAuthState = createFeatureSelector<fromAuth.AuthState>(
  fromAuth.featureKey
);

/**
 * Returns true when the stored access token has passed its expiry time.
 * `recordedAt` may be a string after localStorage rehydration, so we
 * normalise it through `new Date()`.
 */
export const selectIsTokenExpired = createSelector(
  selectAuthState,
  (state): boolean => {
    const token = state.authToken;
    if (!token) return false;
    const expiresAt =
      new Date(token.recordedAt).getTime() +
      token.accessTokenExpiresIn * 1000;
    return Date.now() >= expiresAt;
  }
);

/**
 * True only when a user profile is present AND the stored access token has
 * not yet expired.  Checking expiry here prevents a rehydrated-but-stale
 * session from being treated as authenticated on the next page load.
 */
export const isAuthenticated = createSelector(
  selectAuthState,
  selectIsTokenExpired,
  (state, tokenExpired) =>
    !isNullable(state.loggedInProfile?.userId) && !tokenExpired
);

export const getAccessToken = createSelector(
  selectAuthState,
  (state) => state.authToken?.accessToken
);

export const selectRefreshToken = createSelector(
  selectAuthState,
  (state) => state.authToken?.refreshToken
);

export const selectAuthToken = createSelector(
  selectAuthState,
  (state) => state.authToken
);

export const getLoggedInUserId = createSelector(
  selectAuthState,
  (state) => state.loggedInProfile?.userId ?? undefined
);

/**
 * Returns the ID of the office that is associated with the logged in user,
 * or `undefined` if such a value is not available (e.g. accessing this when
 * no user is logged in).
 */
export const getLoggedInUserOfficeId = createSelector(
  selectAuthState,
  (state) => state.loggedInProfile?.userOfficeId ?? undefined
);

/**
 * Authentication error getter.
 */
export const authError = createSelector(
  selectAuthState,
  (state) => state.authError
);

/** Selects whether the application is being logged in. */
export const isAuthenticating = createSelector(
  selectAuthState,
  (state) => state.isAuthenticating
);
